import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

// src/pages/TicketDeliveryManagement/types.ts
// Kiểu dữ liệu cho phân hệ Giao vé cho đại lý (README_TICKETHANDLE.md — mục 4.3, 4.7, 4.8)

// Trạng thái phiếu giao vé — khớp state machine mục 4.4: Draft=1 ("Chờ duyệt", mục 10.b), Confirmed=2,
// Cancelled=3. AgentConfirmed=4 (mục 13.2): đại lý đã xác nhận nhận vé — chỉ đổi hiển thị, không ảnh
// hưởng tồn kho.
export const TICKET_DELIVERY_STATUS = {
  Draft: 1,
  Confirmed: 2,
  Cancelled: 3,
  AgentConfirmed: 4,
} as const;

export type TicketDeliveryStatus = 1 | 2 | 3 | 4;

// Mục 10.b, README_UPDATE.md — bỏ trạng thái "Nháp", đổi thành "Chờ duyệt".
export const TICKET_DELIVERY_STATUS_LABELS: Record<string, string> = {
  1: st('cho_duyet_text'),
  2: st('da_xac_nhan_text'),
  3: st('da_huy_text'),
  4: st('dai_ly_da_xac_nhan_text'),
};

export const TICKET_DELIVERY_STATUS_COLORS: Record<string, string> = {
  1: 'warning',
  2: 'success',
  3: 'danger',
  4: 'primary',
};

export const TICKET_DELIVERY_STATUS_OPTIONS = [
  { value: 'All', label: st('tat_ca_trang_thai_label') },
  { value: '1', label: st('cho_duyet_text') },
  { value: '2', label: st('da_xac_nhan_text') },
  { value: '3', label: st('da_huy_text') },
  { value: '4', label: st('dai_ly_da_xac_nhan_text') },
];

// 1 dòng của phiếu giao: 1 Symbol + 1 dải số
export interface TicketDeliveryLine {
  id?: number;
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity?: number;
}

// Phiếu giao vé — TicketDeliveryDto (1 phiếu = 1 lần giao cho 1 đại lý, có thể nhiều dòng).
// LƯU Ý: DTO chỉ có `agentId` (logical FK sang system-service), KHÔNG kèm mã/tên đại lý —
// FE map qua helpers/agentLookup.
export interface TicketDelivery {
  id: number;
  deliveryCode: string;
  issuancePlanId: number;
  planCode?: string | null;
  agentId: number;
  status: TicketDeliveryStatus | number;
  statusName?: string | null;
  statusBadgeColor?: string | null;
  note?: string | null;
  totalQuantity?: number | null;
  deliveredBy?: number | null;
  deliveredByName?: string | null;
  deliveredAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
  createdAt?: string | null;
  // Mục 8.g, README_UPDATE.md — giá vé áp dụng cho phiếu này (VND/vé), prefill từ System
  // Configuration lúc lập phiếu, cho sửa tay. Null = phiếu lập trước khi có tính năng này.
  unitPrice?: number | null;
  // Bổ sung — SNAPSHOT % hoa hồng bán hàng riêng của đại lý, đóng băng lúc lập phiếu (thay đổi
  // theo đại lý & theo thời điểm). Null = phiếu lập trước tính năng này. Là nguồn tính HH bán hàng.
  salesCommissionRate?: number | null;
  // Mục 3, README.md (Cập nhật) — đối chiếu Giao/Thu hồi/Bán và công nợ ước tính của CẢ đại lý
  // trong cùng kế hoạch (không chỉ riêng phiếu đang xem) — cùng khái niệm với TicketRecall để 2
  // chiều giao/thu hồi đối chiếu được với nhau.
  agentTotalDeliveredQuantity?: number | null;
  agentTotalRecalledQuantity?: number | null;
  agentTotalSoldQuantity?: number | null;
  agentDebtValue?: number | null;
  // Quy đổi tổng số vé CỦA RIÊNG PHIẾU NÀY ra thành tiền (ưu tiên unitPrice của chính phiếu).
  estimatedValue?: number | null;
  lines?: TicketDeliveryLine[];
}

// Tồn kho hiện tại theo PlanId + Symbol + AgentId (agentId = null là tồn kho tổng công ty)
export interface TicketStockBalance {
  issuancePlanId: number;
  symbol: string;
  agentId?: number | null;
  agentName?: string | null;
  quantity: number;
}

// Body POST /ticket-api/TicketDelivery
export interface CreateTicketDeliveryPayload {
  issuancePlanId: number;
  agentId: number;
  note?: string;
  unitPrice?: number;
  lines: Array<{ symbol: string; rangeStart: string; rangeEnd: string }>;
}

export interface TicketDeliveryFilters {
  planId: string;
  agentId: string;
  status: string;
}

export interface TicketDeliveryPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface TicketDeliveryModalState {
  create: boolean;
  confirm: boolean;
  cancel: boolean;
  detail: boolean;
  agentConfirm: boolean;
}

// Dòng nhập liệu trên form tạo phiếu (giữ nguyên chuỗi người dùng gõ để validate client-side)
export interface DeliveryLineInput {
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
}

/**
 * Validate client-side 1 dòng dải số trước khi submit (validate thật vẫn ở backend — mục 4.5).
 * @returns thông báo lỗi tiếng Việt, hoặc null nếu hợp lệ
 */
export const validateDeliveryLine = (line: DeliveryLineInput): string | null => {
  if (!line.symbol || !line.symbol.trim()) return st('chua_chon_ky_hieu_symbol_text');
  if (!line.rangeStart || !line.rangeStart.trim()) return st('chua_nhap_so_bat_dau_text');
  if (!line.rangeEnd || !line.rangeEnd.trim()) return st('chua_nhap_so_ket_thuc_text');

  if (!/^\d+$/.test(line.rangeStart.trim())) return st('so_bat_dau_chi_duoc_chua_text');
  if (!/^\d+$/.test(line.rangeEnd.trim())) return st('so_ket_thuc_chi_duoc_chua_text');

  const start = Number(line.rangeStart.trim());
  const end = Number(line.rangeEnd.trim());

  if (Number.isNaN(start) || Number.isNaN(end)) return st('dai_so_khong_hop_le_text');
  if (start < 0 || end < 0) return st('dai_so_khong_duoc_am_text');
  if (end < start) return st('so_ket_thuc_phai_lon_hon_text');

  return null;
};

/** Số lượng vé của 1 dòng, tính từ dải số (rangeEnd - rangeStart + 1) */
export const calcLineQuantity = (line: DeliveryLineInput): number => {
  if (validateDeliveryLine(line) !== null) return 0;
  return Number(line.rangeEnd.trim()) - Number(line.rangeStart.trim()) + 1;
};
